/**
 * Định dạng số cho ô nhập.
 *
 * <p>Dùng cho những ô mang <b>tiền, điểm, ngưỡng</b> — nơi con số thường có 6–9 chữ số và đọc
 * liền một dải là nguồn gõ nhầm một số 0. Ô số nhỏ (khung giờ 0–23, phần trăm, số bàn, số ngày)
 * <b>không</b> dùng cái này: thêm dấu ngăn vào một con số hai chữ số chỉ là tiếng ồn.</p>
 *
 * <p>⚠️ <b>Không dùng cho tọa độ.</b> Vĩ độ / kinh độ là số thập phân có thể âm và cần đủ 6 chữ số
 * sau dấu phẩy; nhóm nghìn ở đó vừa sai vừa gây hiểu nhầm.</p>
 */

/**
 * Định dạng số để **hiển thị** — bảng, thẻ số liệu, câu thông báo.
 *
 * <p>Trước 2026-08-30, đúng dòng này được khai lại y hệt ở <b>sáu</b> file
 * (`DashboardPage`, `earning/api`, `EarningRulesPage`, `BillScanDetailModal`, `ReconPage`,
 * `RewardsPage`) với 40 chỗ dùng. Đổi cách hiện tiền — thêm chữ "đ", bỏ số lẻ — phải sửa sáu chỗ,
 * và sót một chỗ thì một màn hiện khác các màn còn lại mà không ai biết cho tới lúc có người soi.</p>
 *
 * <p>⚠️ <b>Giữ nguyên không tùy chọn.</b> `Intl.NumberFormat` mặc định `maximumFractionDigits: 3`;
 * thêm tùy chọn vào đây là đổi cách hiển thị của cả 40 chỗ cùng lúc.</p>
 */
export const VND = new Intl.NumberFormat('vi-VN');

/**
 * Định dạng số cho ô **nhập** — chấp nhận tới 6 chữ số thập phân vì tọa độ cần độ chính xác đó.
 * Khác {@link VND} (chỉ 3 chữ số) một cách cố ý; đừng gộp hai cái làm một.
 */
export const viNumber = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 6 });

/**
 * Cặp `formatter` / `parser` gắn vào `fieldProps` của một ô số.
 *
 * <p>🔴 <b>Parser phải hiểu ĐÚNG quy ước Việt Nam, không phải quy ước Anh — hai bên ngược nhau.</b>
 * `vi-VN` dùng <c>.</c> ngăn nghìn và <c>,</c> ngăn thập phân. Nếu chỉ bóc mọi ký tự không phải chữ số
 * thì <c>"1.234,5"</c> thành <c>12345</c> — sai gấp mười lần mà không có lỗi nào báo. Nên phải bỏ dấu
 * chấm trước, rồi mới đổi dấu phẩy thành dấu chấm cho `Number` đọc.</p>
 */
export const nhomNghin = {
  formatter: (value?: string | number | null): string => {
    if (value === undefined || value === null || value === '') return '';
    const n = Number(value);
    return Number.isFinite(n) ? viNumber.format(n) : String(value);
  },

  parser: (display?: string): number => {
    if (!display) return '' as unknown as number;
    const thuan = display
      .replace(/\./g, '') // bỏ dấu ngăn nghìn
      .replace(/,/g, '.') // dấu thập phân Việt Nam -> dấu thập phân của Number
      .replace(/[^\d.-]/g, ''); // bỏ ký tự đơn vị nếu ô có addon/suffix
    const n = Number(thuan);
    return Number.isFinite(n) ? n : ('' as unknown as number);
  },
} as const;
